import { useState, useEffect } from 'preact/hooks';
import { selectedProperty } from '../state';
import { api } from '../api';

interface Setting {
    id: string;
    property: string;
    name: string;
    value: string;
    status: 'active' | 'inactive';
    created_at: string;
    updated_at: string;
}

interface Goal {
    id: string;
    property: string;
    event_name: string;
    page: string | null;
    display_name: string;
    date_created: string;
    date_updated: string;
}

interface Property {
    id: string;
    name: string;
    domain: string;
    status: 'active' | 'inactive';
    is_default: boolean;
    date_created: string;
    date_updated: string;
}

const VTE_SETTING_TYPES = [
    {
        name: 'event_all',
        label: 'Manda tutti gli eventi',
        description: 'Invia a VTE CRM qualsiasi evento tracciato per il lead'
    },
    {
        name: 'pageview_all',
        label: 'Attiva un processo a ogni pageview',
        description: 'Attiva il processo CRM a ogni visualizzazione di pagina'
    },
    {
        name: 'pageview_count',
        label: 'Attiva un processo all\'ennesima visita dell\'utente',
        description: 'Specifica dopo quante visualizzazioni di pagina attivare il processo CRM'
    },
    {
        name: 'formsend',
        label: 'Attiva un processo all\'invio di un form',
        description: 'Attiva il processo CRM quando un utente invia un form'
    }
];

const TRAFFIC_FILTER_TYPES = [
    {
        name: 'exclude_ip',
        label: 'Escludi per IP',
        description: 'Blocca il traffico proveniente da uno specifico indirizzo IP'
    },
    {
        name: 'allow_hostname',
        label: 'Consenti solo hostname',
        description: 'Se attivo, registra traffico solo dagli hostname permessi'
    },
    {
        name: 'exclude_country',
        label: 'Escludi per paese',
        description: 'Blocca il traffico da un paese specifico usando il codice ISO2'
    },
    {
        name: 'exclude_page',
        label: 'Escludi per pagina o sezione',
        description: 'Blocca il traffico su una pagina o sezione, con supporto a wildcard'
    }
];

const ALL_SETTING_TYPES = [...VTE_SETTING_TYPES, ...TRAFFIC_FILTER_TYPES];
const VTE_FIXED_VALUE_SETTING_TYPES = new Set(['event_all', 'formsend', 'pageview_all']);
const VTE_SETTING_NAMES = new Set(VTE_SETTING_TYPES.map((type) => type.name));
const TRAFFIC_FILTER_SETTING_NAMES = new Set(TRAFFIC_FILTER_TYPES.map((type) => type.name));

const Settings = () => {
    const [settings, setSettings] = useState<Setting[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [selectedType, setSelectedType] = useState<string>('');
    const [formData, setFormData] = useState({
        name: '',
        value: '',
        status: 'active' as 'active' | 'inactive'
    });
    const [error, setError] = useState<string | null>(null);
    const [editingExclusionId, setEditingExclusionId] = useState<string | null>(null);
    const [isAddingNewExclusion, setIsAddingNewExclusion] = useState(false);
    const [selectedExclusionType, setSelectedExclusionType] = useState<string>('');
    const [exclusionFormData, setExclusionFormData] = useState({
        name: '',
        value: '',
        status: 'active' as 'active' | 'inactive'
    });
    const [exclusionError, setExclusionError] = useState<string | null>(null);

    // Goals state
    const [goals, setGoals] = useState<Goal[]>([]);
    const [goalsLoading, setGoalsLoading] = useState(true);
    const [editingGoalId, setEditingGoalId] = useState<string | null>(null);
    const [isAddingNewGoal, setIsAddingNewGoal] = useState(false);
    const [goalFormData, setGoalFormData] = useState({
        event_name: '',
        page: '',
        display_name: '',
        customEvent: ''
    });
    const [goalError, setGoalError] = useState<string | null>(null);

    // Properties state
    const [properties, setProperties] = useState<Property[]>([]);
    const [propertiesLoading, setPropertiesLoading] = useState(true);
    const [editingPropertyId, setEditingPropertyId] = useState<string | null>(null);
    const [isAddingNewProperty, setIsAddingNewProperty] = useState(false);
    const [propertyFormData, setPropertyFormData] = useState({
        name: '',
        domain: '',
        status: 'active' as 'active' | 'inactive',
        is_default: false
    });
    const [propertyError, setPropertyError] = useState<string | null>(null);

    const fetchSettings = async () => {
        if (!selectedProperty.value) {
            setSettings([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const data = await api(`/api/settings?property=${selectedProperty.value}`);
            setSettings(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Errore nel caricamento delle impostazioni');
            console.error('Error fetching settings:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchGoals = async () => {
        if (!selectedProperty.value) {
            setGoals([]);
            setGoalsLoading(false);
            return;
        }

        try {
            setGoalsLoading(true);
            setGoalError(null);
            const data = await api(`/api/goals?property=${selectedProperty.value}`);
            setGoals(data);
        } catch (err) {
            setGoalError(err instanceof Error ? err.message : 'Errore nel caricamento delle conversioni');
            console.error('Error fetching goals:', err);
        } finally {
            setGoalsLoading(false);
        }
    };

    const fetchProperties = async () => {
        try {
            setPropertiesLoading(true);
            setPropertyError(null);
            const data = await api('/properties');
            setProperties(data);
        } catch (err) {
            setPropertyError(err instanceof Error ? err.message : 'Errore nel caricamento delle proprietà');
            console.error('Error fetching properties:', err);
        } finally {
            setPropertiesLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
        fetchGoals();
        fetchProperties();
    }, [selectedProperty.value]);

    const getSettingLabel = (name: string) => {
        const type = ALL_SETTING_TYPES.find(t => t.name === name);
        return type ? type.label : name;
    };

    const getTrafficFilterPlaceholder = (name: string) => {
        if (name === 'exclude_ip') return 'es. 203.0.113.42';
        if (name === 'allow_hostname') return 'es. *.example.com oppure *example.com';
        if (name === 'exclude_country') return 'es. IT';
        if (name === 'exclude_page') return 'es. /admin/*';
        return 'Inserisci il valore';
    };

    const getTrafficFilterHelpText = (name: string) => {
        if (name === 'allow_hostname') {
            return 'Funziona come allow list: se è presente almeno un hostname attivo, tutto il resto viene escluso.';
        }
        if (name === 'exclude_country') {
            return 'Usa il codice paese ISO2 in maiuscolo, per esempio IT, DE, FR.';
        }
        if (name === 'exclude_page') {
            return 'Puoi usare * come wildcard, per esempio /blog/* o /admin/*.';
        }
        return null;
    };

    const getEventLabel = (eventName: string) => {
        const labels: { [key: string]: string } = {
            'pageview': 'Visualizzazione Pagina',
            'form_submit': 'Invio Form'
        };
        return labels[eventName] || eventName;
    };

    const handleEdit = (setting: Setting) => {
        setEditingId(setting.id);
        setSelectedType(setting.name);
        setFormData({
            name: setting.name,
            value: setting.value,
            status: setting.status
        });
        setIsAddingNew(false);
        setError(null);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setIsAddingNew(false);
        setSelectedType('');
        setFormData({ name: '', value: '', status: 'active' });
        setError(null);
    };

    const handleAddNew = () => {
        setIsAddingNew(true);
        setEditingId(null);
        setSelectedType('');
        setFormData({ name: '', value: '', status: 'active' });
        setError(null);
    };

    const handleTypeSelect = (typeName: string) => {
        setSelectedType(typeName);
        setFormData({ 
            ...formData, 
            name: typeName,
            value: VTE_FIXED_VALUE_SETTING_TYPES.has(typeName) ? '1' : ''
        });
    };

    const handleSave = async () => {
        if (!formData.name || (!formData.value && !VTE_FIXED_VALUE_SETTING_TYPES.has(formData.name))) {
            setError('Nome e valore sono obbligatori');
            return;
        }
        
        // Ensure fixed-value triggers always store "1"
        const dataToSave = VTE_FIXED_VALUE_SETTING_TYPES.has(formData.name)
            ? { ...formData, value: '1' }
            : formData;

        try {
            setError(null);
            let response;
            
            if (isAddingNew) {
                response = await api('/api/settings', {
                    method: 'POST',
                    body: JSON.stringify({
                        property: selectedProperty.value,
                        ...dataToSave
                    })
                });
            } else if (editingId) {
                response = await api(`/api/settings/${editingId}`, {
                    method: 'POST',
                    body: JSON.stringify(dataToSave)
                });
            }

            if (response && response.error) {
                throw new Error(response.error || 'Errore nel salvataggio dell\'impostazione');
            }

            await fetchSettings();
            handleCancelEdit();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Errore nel salvataggio dell\'impostazione');
            console.error('Error saving setting:', err);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Sei sicuro di voler eliminare questa impostazione?')) {
            return;
        }

        try {
            setError(null);
            const response = await api(`/api/settings/${id}`, {
                method: 'DELETE'
            });

            if (response.error) {
                throw new Error(response.error || 'Errore nell\'eliminazione dell\'impostazione');
            }

            await fetchSettings();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Errore nell\'eliminazione dell\'impostazione');
            console.error('Error deleting setting:', err);
        }
    };

    const handleEditExclusion = (setting: Setting) => {
        setEditingExclusionId(setting.id);
        setSelectedExclusionType(setting.name);
        setExclusionFormData({
            name: setting.name,
            value: setting.value,
            status: setting.status
        });
        setIsAddingNewExclusion(false);
        setExclusionError(null);
    };

    const handleCancelExclusionEdit = () => {
        setEditingExclusionId(null);
        setIsAddingNewExclusion(false);
        setSelectedExclusionType('');
        setExclusionFormData({ name: '', value: '', status: 'active' });
        setExclusionError(null);
    };

    const handleAddNewExclusion = () => {
        setIsAddingNewExclusion(true);
        setEditingExclusionId(null);
        setSelectedExclusionType('');
        setExclusionFormData({ name: '', value: '', status: 'active' });
        setExclusionError(null);
    };

    const handleExclusionTypeSelect = (typeName: string) => {
        setSelectedExclusionType(typeName);
        setExclusionFormData({
            ...exclusionFormData,
            name: typeName,
            value: '',
            status: 'active'
        });
    };

    const handleSaveExclusion = async () => {
        if (!exclusionFormData.name || !exclusionFormData.value) {
            setExclusionError('Nome e valore sono obbligatori');
            return;
        }

        try {
            setExclusionError(null);
            let response;

            if (isAddingNewExclusion) {
                response = await api('/api/settings', {
                    method: 'POST',
                    body: JSON.stringify({
                        property: selectedProperty.value,
                        ...exclusionFormData
                    })
                });
            } else if (editingExclusionId) {
                response = await api(`/api/settings/${editingExclusionId}`, {
                    method: 'POST',
                    body: JSON.stringify(exclusionFormData)
                });
            }

            if (response && response.error) {
                throw new Error(response.error || 'Errore nel salvataggio del filtro traffico');
            }

            await fetchSettings();
            handleCancelExclusionEdit();
        } catch (err) {
            setExclusionError(err instanceof Error ? err.message : 'Errore nel salvataggio del filtro traffico');
            console.error('Error saving traffic filter:', err);
        }
    };

    const handleDeleteExclusion = async (id: string) => {
        if (!confirm('Sei sicuro di voler eliminare questo filtro traffico?')) {
            return;
        }

        try {
            setExclusionError(null);
            const response = await api(`/api/settings/${id}`, {
                method: 'DELETE'
            });

            if (response.error) {
                throw new Error(response.error || 'Errore nell\'eliminazione del filtro traffico');
            }

            await fetchSettings();
        } catch (err) {
            setExclusionError(err instanceof Error ? err.message : 'Errore nell\'eliminazione del filtro traffico');
            console.error('Error deleting traffic filter:', err);
        }
    };

    // Goals handlers
    const handleAddNewGoal = () => {
        setIsAddingNewGoal(true);
        setEditingGoalId(null);
        setGoalFormData({ event_name: '', page: '', display_name: '', customEvent: '' });
        setGoalError(null);
    };

    const handleCancelGoalEdit = () => {
        setEditingGoalId(null);
        setIsAddingNewGoal(false);
        setGoalFormData({ event_name: '', page: '', display_name: '', customEvent: '' });
        setGoalError(null);
    };

    const handleEditGoal = (goal: Goal) => {
        setEditingGoalId(goal.id);
        setGoalFormData({
            event_name: goal.event_name,
            page: goal.page || '',
            display_name: goal.display_name,
            customEvent: (goal.event_name !== 'pageview' && goal.event_name !== 'form_submit') ? goal.event_name : ''
        });
        setIsAddingNewGoal(false);
        setGoalError(null);
    };

    const handleSaveGoal = async () => {
        const eventName = goalFormData.event_name === 'custom' 
            ? goalFormData.customEvent 
            : goalFormData.event_name;

        if (!eventName || !goalFormData.display_name) {
            setGoalError('Nome evento e nome visualizzato sono obbligatori');
            return;
        }

        /* if ((goalFormData.event_name === 'pageview' || goalFormData.event_name === 'form_submit') && !goalFormData.page) {
            setGoalError('Il campo pagina/selettore è obbligatorio per questo tipo di evento');
            return;
        } */

        try {
            setGoalError(null);
            let response;

            const dataToSave = {
                event_name: eventName,
                page: goalFormData.page || null,
                display_name: goalFormData.display_name
            };

            if (isAddingNewGoal) {
                response = await api('/api/goals', {
                    method: 'POST',
                    body: JSON.stringify({
                        property: selectedProperty.value,
                        ...dataToSave
                    })
                });
            } else if (editingGoalId) {
                response = await api(`/api/goals/${editingGoalId}`, {
                    method: 'POST',
                    body: JSON.stringify(dataToSave)
                });
            }

            if (response && response.error) {
                throw new Error(response.error || 'Errore nel salvataggio della conversione');
            }

            await fetchGoals();
            handleCancelGoalEdit();
        } catch (err) {
            setGoalError(err instanceof Error ? err.message : 'Errore nel salvataggio della conversione');
            console.error('Error saving goal:', err);
        }
    };

    const handleDeleteGoal = async (id: string) => {
        if (!confirm('Sei sicuro di voler eliminare questa conversione?')) {
            return;
        }

        try {
            setGoalError(null);
            const response = await api(`/api/goals/${id}`, {
                method: 'DELETE'
            });

            if (response.error) {
                throw new Error(response.error || 'Errore nell\'eliminazione della conversione');
            }

            await fetchGoals();
        } catch (err) {
            setGoalError(err instanceof Error ? err.message : 'Errore nell\'eliminazione della conversione');
            console.error('Error deleting goal:', err);
        }
    };

    // Properties handlers
    const handleAddNewProperty = () => {
        setIsAddingNewProperty(true);
        setEditingPropertyId(null);
        setPropertyFormData({ name: '', domain: '', status: 'active', is_default: false });
        setPropertyError(null);
    };

    const handleCancelPropertyEdit = () => {
        setEditingPropertyId(null);
        setIsAddingNewProperty(false);
        setPropertyFormData({ name: '', domain: '', status: 'active', is_default: false });
        setPropertyError(null);
    };

    const handleEditProperty = (property: Property) => {
        setEditingPropertyId(property.id);
        setPropertyFormData({
            name: property.name,
            domain: property.domain,
            status: property.status,
            is_default: property.is_default
        });
        setIsAddingNewProperty(false);
        setPropertyError(null);
    };

    const handleSaveProperty = async () => {
        if (!propertyFormData.name || !propertyFormData.domain) {
            setPropertyError('Nome e dominio sono obbligatori');
            return;
        }

        try {
            setPropertyError(null);
            let response;

            if (isAddingNewProperty) {
                response = await api('/properties', {
                    method: 'POST',
                    body: JSON.stringify({
                        name: propertyFormData.name,
                        domain: propertyFormData.domain,
                        status: propertyFormData.status,
                        is_default: propertyFormData.is_default
                    })
                });
            } else if (editingPropertyId) {
                response = await api(`/properties/${editingPropertyId}`, {
                    method: 'PATCH',
                    body: JSON.stringify({
                        name: propertyFormData.name,
                        domain: propertyFormData.domain,
                        status: propertyFormData.status,
                        is_default: propertyFormData.is_default
                    })
                });
            }

            if (response && response.error) {
                throw new Error(response.error || 'Errore nel salvataggio della proprietà');
            }

            await fetchProperties();
            handleCancelPropertyEdit();
        } catch (err) {
            setPropertyError(err instanceof Error ? err.message : 'Errore nel salvataggio della proprietà');
            console.error('Error saving property:', err);
        }
    };

    const handleDeleteProperty = async (id: string) => {
        if (!confirm('Sei sicuro di voler eliminare questa proprietà? Tutti i dati associati verranno persi.')) {
            return;
        }

        try {
            setPropertyError(null);
            const response = await api(`/properties/${id}`, {
                method: 'DELETE'
            });

            if (response.error) {
                throw new Error(response.error || 'Errore nell\'eliminazione della proprietà');
            }

            await fetchProperties();
        } catch (err) {
            setPropertyError(err instanceof Error ? err.message : 'Errore nell\'eliminazione della proprietà');
            console.error('Error deleting property:', err);
        }
    };

    if (!selectedProperty.value) {
        return (
            <div className="card">
                <h2 className="card-title">Impostazioni</h2>
                <p className="card-description">Seleziona una proprietà per gestire le impostazioni.</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="card">
                <h2 className="card-title">Impostazioni</h2>
                <p className="card-description">Caricamento impostazioni...</p>
            </div>
        );
    }

    const trackingScriptUrl = typeof window === 'undefined'
        ? `/client.js?pid=${selectedProperty.value}`
        : new URL(`/client.js?pid=${selectedProperty.value}`, window.location.origin).toString();
    const trackingScriptTag = `<script src="${trackingScriptUrl}" defer></script>`;

    const handleCopyScript = () => {
        navigator.clipboard.writeText(trackingScriptTag).then(() => {
            alert('Script copiato negli appunti!');
        }).catch(err => {
            console.error('Errore nella copia dello script:', err);
            alert('Errore nella copia dello script');
        });
    };

    const handleCopyConsentSnippet = () => {
        const consentSnippet = `${trackingScriptTag}
<script>
  const ANON_ID_KEY = "branzino_anonymous_id";
  const getOrCreateAnonymousId = () => {
    let id = localStorage.getItem(ANON_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(ANON_ID_KEY, id);
    }
    return id;
  };

  // Chiamare quando il banner segnala consenso analytics accettato
  window.enableBranzinoTrackingAfterConsent = () => {
    const anonymousId = getOrCreateAnonymousId();
    window.branzino?.enablePseudonymousTracking?.(anonymousId);
  };

  // Chiamare quando il banner segnala revoca/rifiuto consenso analytics
  window.disableBranzinoTrackingAfterConsentRevoked = () => {
    localStorage.removeItem(ANON_ID_KEY);
    window.branzino?.disablePseudonymousTracking?.();
  };
</script>`;

        navigator.clipboard.writeText(consentSnippet).then(() => {
            alert('Snippet consenso copiato negli appunti!');
        }).catch(err => {
            console.error('Errore nella copia dello snippet consenso:', err);
            alert('Errore nella copia dello snippet consenso');
        });
    };

    const vteSettings = settings.filter((setting) => VTE_SETTING_NAMES.has(setting.name));
    const trafficFilterSettings = settings.filter((setting) => TRAFFIC_FILTER_SETTING_NAMES.has(setting.name));
    const isEditingAnySetting = Boolean(editingId || isAddingNew || editingExclusionId || isAddingNewExclusion);

    return (
        <div className="settings-container">
            {/* Script di tracciamento */}
            <div className="card" style={{ marginBottom: '24px' }}>
                <div className="card-header">
                    <div>
                        <h2 className="card-title">Script di Tracciamento</h2>
                        <p className="card-description">
                            Copia questo script e incollalo nel tag &lt;head&gt; del tuo sito web per iniziare il tracciamento.
                        </p>
                    </div>
                </div>
                
                <div style={{
                    padding: '16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    position: 'relative'
                }}>
                    <code style={{ 
                        display: 'block',
                        wordBreak: 'break-all',
                        color: '#333'
                    }}>
                        {trackingScriptTag}
                    </code>
                    <button
                        className="button button-primary"
                        onClick={handleCopyScript}
                        style={{
                            marginTop: '12px'
                        }}
                    >
                        📋 Copia Script
                    </button>
                </div>

                <div style={{
                    marginTop: '16px',
                    padding: '16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    fontFamily: 'monospace',
                    fontSize: '13px'
                }}>
                    <p style={{ margin: 0, marginBottom: '8px', fontFamily: 'inherit', color: '#333' }}>
                        Esempio integrazione consenso (abilita pseudonimo solo dopo accettazione banner):
                    </p>
                    <code style={{
                        display: 'block',
                        whiteSpace: 'pre-wrap',
                        color: '#333'
                    }}>
{`window.enableBranzinoTrackingAfterConsent = () => {
  const id = localStorage.getItem("branzino_anonymous_id") || crypto.randomUUID();
  localStorage.setItem("branzino_anonymous_id", id);
  window.branzino?.enablePseudonymousTracking?.(id);
};`}
                    </code>
                    <button
                        className="button button-primary"
                        onClick={handleCopyConsentSnippet}
                        style={{ marginTop: '12px' }}
                    >
                        📋 Copia Snippet Consenso
                    </button>
                </div>
            </div>

            <div className="card">
                <div className="card-header">
                    <div>
                        <h2 className="card-title">Impostazioni Integrazione VTE CRM</h2>
                        <p className="card-description">
                            Configura quali processi attivare nel CRM in base al comportamento degli utenti.
                        </p>
                    </div>
                    {!isAddingNew && !editingId && !isAddingNewExclusion && !editingExclusionId && (
                        <button 
                            className="button button-primary"
                            onClick={handleAddNew}
                        >
                            Aggiungi Nuova Impostazione
                        </button>
                    )}
                </div>

                {error && (
                    <div className="error-message" style={{ 
                        padding: '12px', 
                        marginBottom: '16px', 
                        backgroundColor: '#fee', 
                        border: '1px solid #fcc',
                        borderRadius: '4px',
                        color: '#c00'
                    }}>
                        {error}
                    </div>
                )}

                {(isAddingNew || editingId) && (
                    <div className="settings-form" style={{
                        padding: '20px',
                        marginBottom: '20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6'
                    }}>
                        <h3 style={{ marginTop: 0 }}>
                            {isAddingNew ? 'Aggiungi Nuova Impostazione' : 'Modifica Impostazione'}
                        </h3>
                        <div style={{ display: 'grid', gap: '16px' }}>
                            {isAddingNew && !selectedType && (
                                <div>
                                    <label style={{ display: 'block', marginBottom: '12px', fontWeight: 500 }}>
                                        Seleziona il tipo di processo da attivare
                                    </label>
                                    <div style={{ display: 'grid', gap: '12px' }}>
                                        {VTE_SETTING_TYPES.map((type) => (
                                            <div
                                                key={type.name}
                                                onClick={() => handleTypeSelect(type.name)}
                                                style={{
                                                    padding: '16px',
                                                    border: '2px solid #dee2e6',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    backgroundColor: '#fff'
                                                }}
                                                onMouseOver={(e) => {
                                                    (e.currentTarget as HTMLElement).style.borderColor = '#0066cc';
                                                    (e.currentTarget as HTMLElement).style.backgroundColor = '#f0f7ff';
                                                }}
                                                onMouseOut={(e) => {
                                                    (e.currentTarget as HTMLElement).style.borderColor = '#dee2e6';
                                                    (e.currentTarget as HTMLElement).style.backgroundColor = '#fff';
                                                }}
                                            >
                                                <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '15px' }}>
                                                    {type.label}
                                                </div>
                                                <div style={{ fontSize: '13px', color: '#666' }}>
                                                    {type.description}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {(selectedType || editingId) && (
                                <>
                                    {isAddingNew && (
                                        <div style={{
                                            padding: '12px',
                                            backgroundColor: '#e7f3ff',
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}>
                                            <strong>Tipo selezionato:</strong> {getSettingLabel(formData.name)}
                                        </div>
                                    )}
                                    
                                    {!VTE_FIXED_VALUE_SETTING_TYPES.has(formData.name) && (
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                                                Valore
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.value}
                                                onChange={(e) => setFormData({ ...formData, value: (e.target as HTMLInputElement).value })}
                                                placeholder={formData.name === 'pageview_count' ? 'es. 5 (numero di visite)' : 'Inserisci il valore'}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    border: '1px solid #dee2e6',
                                                    borderRadius: '4px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                            {formData.name === 'pageview_count' && (
                                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                    Inserisci il numero di visualizzazioni dopo cui attivare il processo
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                                            Stato
                                        </label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: (e.target as HTMLSelectElement).value as 'active' | 'inactive' })}
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: '1px solid #dee2e6',
                                                borderRadius: '4px',
                                                fontSize: '14px'
                                            }}
                                        >
                                            <option value="active">Attivo</option>
                                            <option value="inactive">Inattivo</option>
                                        </select>
                                    </div>
                                </>
                            )}
                            
                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                {(selectedType || editingId) && (
                                    <button 
                                        className="button button-primary"
                                        onClick={handleSave}
                                    >
                                        Salva
                                    </button>
                                )}
                                <button 
                                    className="button"
                                    onClick={handleCancelEdit}
                                >
                                    Annulla
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {vteSettings.length === 0 ? (
                    <p className="card-description">Nessuna impostazione configurata. Aggiungi la tua prima impostazione per iniziare.</p>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Tipo</th>
                                <th>Valore</th>
                                <th>Stato</th>
                                <th>Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {vteSettings.map((setting) => (
                                <tr key={setting.id}>
                                    <td>
                                        <div>
                                            <strong>{getSettingLabel(setting.name)}</strong>
                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                                {setting.name}
                                            </div>
                                        </div>
                                    </td>
                                    <td>{setting.value}</td>
                                    <td>
                                        <span className={`badge badge-${setting.status === 'active' ? 'success' : 'inactive'}`}>
                                            {setting.status === 'active' ? 'Attivo' : 'Inattivo'}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                onClick={() => handleEdit(setting)}
                                                disabled={isEditingAnySetting}
                                                style={{
                                                    padding: '6px 12px',
                                                    fontSize: '13px',
                                                    border: '1px solid #0066cc',
                                                    backgroundColor: '#fff',
                                                    color: '#0066cc',
                                                    borderRadius: '4px',
                                                    cursor: isEditingAnySetting ? 'not-allowed' : 'pointer',
                                                    opacity: isEditingAnySetting ? 0.5 : 1,
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseOver={(e) => {
                                                    if (!isEditingAnySetting) {
                                                        (e.currentTarget as HTMLElement).style.backgroundColor = '#0066cc';
                                                        (e.currentTarget as HTMLElement).style.color = '#fff';
                                                    }
                                                }}
                                                onMouseOut={(e) => {
                                                    (e.currentTarget as HTMLElement).style.backgroundColor = '#fff';
                                                    (e.currentTarget as HTMLElement).style.color = '#0066cc';
                                                }}
                                            >
                                                Modifica
                                            </button>
                                            <button
                                                onClick={() => handleDelete(setting.id)}
                                                disabled={isEditingAnySetting}
                                                style={{
                                                    padding: '6px 12px',
                                                    fontSize: '13px',
                                                    border: '1px solid #dc3545',
                                                    backgroundColor: '#fff',
                                                    color: '#dc3545',
                                                    borderRadius: '4px',
                                                    cursor: isEditingAnySetting ? 'not-allowed' : 'pointer',
                                                    opacity: isEditingAnySetting ? 0.5 : 1,
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseOver={(e) => {
                                                    if (!isEditingAnySetting) {
                                                        (e.currentTarget as HTMLElement).style.backgroundColor = '#dc3545';
                                                        (e.currentTarget as HTMLElement).style.color = '#fff';
                                                    }
                                                }}
                                                onMouseOut={(e) => {
                                                    (e.currentTarget as HTMLElement).style.backgroundColor = '#fff';
                                                    (e.currentTarget as HTMLElement).style.color = '#dc3545';
                                                }}
                                            >
                                                Elimina
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="card" style={{ marginTop: '24px' }}>
                <div className="card-header">
                    <div>
                        <h2 className="card-title">Esclusione Traffico</h2>
                        <p className="card-description">
                            Escludi traffico interno o indesiderato per IP, hostname, paese o pagina.
                        </p>
                    </div>
                    {!isAddingNewExclusion && !editingExclusionId && !isAddingNew && !editingId && (
                        <button
                            className="button button-primary"
                            onClick={handleAddNewExclusion}
                        >
                            Aggiungi Filtro
                        </button>
                    )}
                </div>

                <div style={{
                    padding: '12px',
                    marginBottom: '16px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#555'
                }}>
                    Gli hostname funzionano come allow list: se ne configuri almeno uno attivo, il tracking viene registrato solo per gli hostname che combaciano.
                </div>

                {exclusionError && (
                    <div className="error-message" style={{
                        padding: '12px',
                        marginBottom: '16px',
                        backgroundColor: '#fee',
                        border: '1px solid #fcc',
                        borderRadius: '4px',
                        color: '#c00'
                    }}>
                        {exclusionError}
                    </div>
                )}

                {(isAddingNewExclusion || editingExclusionId) && (
                    <div className="settings-form" style={{
                        padding: '20px',
                        marginBottom: '20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6'
                    }}>
                        <h3 style={{ marginTop: 0 }}>
                            {isAddingNewExclusion ? 'Aggiungi Filtro Traffico' : 'Modifica Filtro Traffico'}
                        </h3>
                        <div style={{ display: 'grid', gap: '16px' }}>
                            {isAddingNewExclusion && !selectedExclusionType && (
                                <div>
                                    <label style={{ display: 'block', marginBottom: '12px', fontWeight: 500 }}>
                                        Seleziona il tipo di esclusione
                                    </label>
                                    <div style={{ display: 'grid', gap: '12px' }}>
                                        {TRAFFIC_FILTER_TYPES.map((type) => (
                                            <div
                                                key={type.name}
                                                onClick={() => handleExclusionTypeSelect(type.name)}
                                                style={{
                                                    padding: '16px',
                                                    border: '2px solid #dee2e6',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    backgroundColor: '#fff'
                                                }}
                                                onMouseOver={(e) => {
                                                    (e.currentTarget as HTMLElement).style.borderColor = '#0066cc';
                                                    (e.currentTarget as HTMLElement).style.backgroundColor = '#f0f7ff';
                                                }}
                                                onMouseOut={(e) => {
                                                    (e.currentTarget as HTMLElement).style.borderColor = '#dee2e6';
                                                    (e.currentTarget as HTMLElement).style.backgroundColor = '#fff';
                                                }}
                                            >
                                                <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '15px' }}>
                                                    {type.label}
                                                </div>
                                                <div style={{ fontSize: '13px', color: '#666' }}>
                                                    {type.description}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {(selectedExclusionType || editingExclusionId) && (
                                <>
                                    {isAddingNewExclusion && (
                                        <div style={{
                                            padding: '12px',
                                            backgroundColor: '#e7f3ff',
                                            borderRadius: '6px',
                                            fontSize: '14px'
                                        }}>
                                            <strong>Filtro selezionato:</strong> {getSettingLabel(exclusionFormData.name)}
                                        </div>
                                    )}

                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                                            Valore
                                        </label>
                                        <input
                                            type="text"
                                            value={exclusionFormData.value}
                                            onChange={(e) => setExclusionFormData({ ...exclusionFormData, value: (e.target as HTMLInputElement).value })}
                                            placeholder={getTrafficFilterPlaceholder(exclusionFormData.name)}
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: '1px solid #dee2e6',
                                                borderRadius: '4px',
                                                fontSize: '14px'
                                            }}
                                        />
                                        {getTrafficFilterHelpText(exclusionFormData.name) && (
                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                {getTrafficFilterHelpText(exclusionFormData.name)}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                                            Stato
                                        </label>
                                        <select
                                            value={exclusionFormData.status}
                                            onChange={(e) => setExclusionFormData({ ...exclusionFormData, status: (e.target as HTMLSelectElement).value as 'active' | 'inactive' })}
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: '1px solid #dee2e6',
                                                borderRadius: '4px',
                                                fontSize: '14px'
                                            }}
                                        >
                                            <option value="active">Attivo</option>
                                            <option value="inactive">Inattivo</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                {(selectedExclusionType || editingExclusionId) && (
                                    <button
                                        className="button button-primary"
                                        onClick={handleSaveExclusion}
                                    >
                                        Salva
                                    </button>
                                )}
                                <button
                                    className="button"
                                    onClick={handleCancelExclusionEdit}
                                >
                                    Annulla
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {trafficFilterSettings.length === 0 ? (
                    <p className="card-description">Nessun filtro traffico configurato.</p>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Tipo</th>
                                <th>Valore</th>
                                <th>Stato</th>
                                <th>Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {trafficFilterSettings.map((setting) => (
                                <tr key={setting.id}>
                                    <td>
                                        <div>
                                            <strong>{getSettingLabel(setting.name)}</strong>
                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                                {setting.name}
                                            </div>
                                        </div>
                                    </td>
                                    <td>{setting.value}</td>
                                    <td>
                                        <span className={`badge badge-${setting.status === 'active' ? 'success' : 'inactive'}`}>
                                            {setting.status === 'active' ? 'Attivo' : 'Inattivo'}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                onClick={() => handleEditExclusion(setting)}
                                                disabled={isEditingAnySetting}
                                                style={{
                                                    padding: '6px 12px',
                                                    fontSize: '13px',
                                                    border: '1px solid #0066cc',
                                                    backgroundColor: '#fff',
                                                    color: '#0066cc',
                                                    borderRadius: '4px',
                                                    cursor: isEditingAnySetting ? 'not-allowed' : 'pointer',
                                                    opacity: isEditingAnySetting ? 0.5 : 1,
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseOver={(e) => {
                                                    if (!isEditingAnySetting) {
                                                        (e.currentTarget as HTMLElement).style.backgroundColor = '#0066cc';
                                                        (e.currentTarget as HTMLElement).style.color = '#fff';
                                                    }
                                                }}
                                                onMouseOut={(e) => {
                                                    (e.currentTarget as HTMLElement).style.backgroundColor = '#fff';
                                                    (e.currentTarget as HTMLElement).style.color = '#0066cc';
                                                }}
                                            >
                                                Modifica
                                            </button>
                                            <button
                                                onClick={() => handleDeleteExclusion(setting.id)}
                                                disabled={isEditingAnySetting}
                                                style={{
                                                    padding: '6px 12px',
                                                    fontSize: '13px',
                                                    border: '1px solid #dc3545',
                                                    backgroundColor: '#fff',
                                                    color: '#dc3545',
                                                    borderRadius: '4px',
                                                    cursor: isEditingAnySetting ? 'not-allowed' : 'pointer',
                                                    opacity: isEditingAnySetting ? 0.5 : 1,
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseOver={(e) => {
                                                    if (!isEditingAnySetting) {
                                                        (e.currentTarget as HTMLElement).style.backgroundColor = '#dc3545';
                                                        (e.currentTarget as HTMLElement).style.color = '#fff';
                                                    }
                                                }}
                                                onMouseOut={(e) => {
                                                    (e.currentTarget as HTMLElement).style.backgroundColor = '#fff';
                                                    (e.currentTarget as HTMLElement).style.color = '#dc3545';
                                                }}
                                            >
                                                Elimina
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Goals (Conversioni) Section */}
            <div className="card" style={{ marginTop: '24px' }}>
                <div className="card-header">
                    <div>
                        <h2 className="card-title">Conversioni (Goals)</h2>
                        <p className="card-description">
                            Configura gli eventi da tracciare come conversioni.
                        </p>
                    </div>
                    {!isAddingNewGoal && !editingGoalId && (
                        <button 
                            className="button button-primary"
                            onClick={handleAddNewGoal}
                        >
                            Aggiungi Nuova Conversione
                        </button>
                    )}
                </div>

                {goalError && (
                    <div className="error-message" style={{ 
                        padding: '12px', 
                        marginBottom: '16px', 
                        backgroundColor: '#fee', 
                        border: '1px solid #fcc',
                        borderRadius: '4px',
                        color: '#c00'
                    }}>
                        {goalError}
                    </div>
                )}

                {(isAddingNewGoal || editingGoalId) && (
                    <div className="settings-form" style={{
                        padding: '20px',
                        marginBottom: '20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6'
                    }}>
                        <h3 style={{ marginTop: 0 }}>
                            {isAddingNewGoal ? 'Aggiungi Nuova Conversione' : 'Modifica Conversione'}
                        </h3>
                        <div style={{ display: 'grid', gap: '16px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                                    Nome Visualizzato
                                </label>
                                <input
                                    type="text"
                                    value={goalFormData.display_name}
                                    onChange={(e) => setGoalFormData({ ...goalFormData, display_name: (e.target as HTMLInputElement).value })}
                                    placeholder="es. Iscrizione Newsletter, Download PDF..."
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                                    Tipo di Evento
                                </label>
                                <select
                                    value={goalFormData.event_name}
                                    onChange={(e) => setGoalFormData({ ...goalFormData, event_name: (e.target as HTMLSelectElement).value, page: '' })}
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="">Seleziona un tipo di evento</option>
                                    <option value="pageview">Visualizzazione Pagina</option>
                                    <option value="form_submit">Invio Form</option>
                                    <option value="custom">Evento Personalizzato</option>
                                </select>
                            </div>

                            {goalFormData.event_name === 'custom' && (
                                <div>
                                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                                        Nome Evento Personalizzato
                                    </label>
                                    <input
                                        type="text"
                                        value={goalFormData.customEvent}
                                        onChange={(e) => setGoalFormData({ ...goalFormData, customEvent: (e.target as HTMLInputElement).value })}
                                        placeholder="es. button_click, video_play..."
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    />
                                </div>
                            )}

                            {goalFormData.event_name === 'pageview' && (
                                <div>
                                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                                        Percorso Pagina
                                    </label>
                                    <input
                                        type="text"
                                        value={goalFormData.page}
                                        onChange={(e) => setGoalFormData({ ...goalFormData, page: (e.target as HTMLInputElement).value })}
                                        placeholder="es. /thank-you, /contatti"
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    />
                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                        Inserisci il percorso della pagina (es. /thank-you)
                                    </div>
                                </div>
                            )}

                            {goalFormData.event_name === 'form_submit' && (
                                <div>
                                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                                        Selettore CSS del Form
                                    </label>
                                    <input
                                        type="text"
                                        value={goalFormData.page}
                                        onChange={(e) => setGoalFormData({ ...goalFormData, page: (e.target as HTMLInputElement).value })}
                                        placeholder="es. #contact-form, .newsletter-form"
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #dee2e6',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    />
                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                        Inserisci il selettore CSS del form (es. #contact-form)
                                    </div>
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                <button 
                                    className="button button-primary"
                                    onClick={handleSaveGoal}
                                >
                                    Salva
                                </button>
                                <button 
                                    className="button"
                                    onClick={handleCancelGoalEdit}
                                >
                                    Annulla
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {goalsLoading ? (
                    <p className="card-description">Caricamento conversioni...</p>
                ) : goals.length === 0 ? (
                    <p className="card-description">Nessuna conversione configurata. Aggiungi la tua prima conversione per iniziare.</p>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Tipo Evento</th>
                                <th>Pagina/Selettore</th>
                                <th>Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {goals.map((goal) => (
                                <tr key={goal.id}>
                                    <td>
                                        <strong>{goal.display_name}</strong>
                                    </td>
                                    <td>{getEventLabel(goal.event_name)}</td>
                                    <td>
                                        {goal.page ? (
                                            <code style={{ 
                                                backgroundColor: '#f4f4f4', 
                                                padding: '2px 6px', 
                                                borderRadius: '3px',
                                                fontSize: '12px'
                                            }}>
                                                {goal.page}
                                            </code>
                                        ) : (
                                            <span style={{ color: '#999' }}>-</span>
                                        )}
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                onClick={() => handleEditGoal(goal)}
                                                disabled={!!editingGoalId || isAddingNewGoal}
                                                style={{
                                                    padding: '6px 12px',
                                                    fontSize: '13px',
                                                    border: '1px solid #0066cc',
                                                    backgroundColor: '#fff',
                                                    color: '#0066cc',
                                                    borderRadius: '4px',
                                                    cursor: editingGoalId || isAddingNewGoal ? 'not-allowed' : 'pointer',
                                                    opacity: editingGoalId || isAddingNewGoal ? 0.5 : 1,
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseOver={(e) => {
                                                    if (!editingGoalId && !isAddingNewGoal) {
                                                        (e.currentTarget as HTMLElement).style.backgroundColor = '#0066cc';
                                                        (e.currentTarget as HTMLElement).style.color = '#fff';
                                                    }
                                                }}
                                                onMouseOut={(e) => {
                                                    (e.currentTarget as HTMLElement).style.backgroundColor = '#fff';
                                                    (e.currentTarget as HTMLElement).style.color = '#0066cc';
                                                }}
                                            >
                                                Modifica
                                            </button>
                                            <button
                                                onClick={() => handleDeleteGoal(goal.id)}
                                                disabled={!!editingGoalId || isAddingNewGoal}
                                                style={{
                                                    padding: '6px 12px',
                                                    fontSize: '13px',
                                                    border: '1px solid #dc3545',
                                                    backgroundColor: '#fff',
                                                    color: '#dc3545',
                                                    borderRadius: '4px',
                                                    cursor: editingGoalId || isAddingNewGoal ? 'not-allowed' : 'pointer',
                                                    opacity: editingGoalId || isAddingNewGoal ? 0.5 : 1,
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseOver={(e) => {
                                                    if (!editingGoalId && !isAddingNewGoal) {
                                                        (e.currentTarget as HTMLElement).style.backgroundColor = '#dc3545';
                                                        (e.currentTarget as HTMLElement).style.color = '#fff';
                                                    }
                                                }}
                                                onMouseOut={(e) => {
                                                    (e.currentTarget as HTMLElement).style.backgroundColor = '#fff';
                                                    (e.currentTarget as HTMLElement).style.color = '#dc3545';
                                                }}
                                            >
                                                Elimina
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Properties Section */}
            <div className="card" style={{ marginTop: '24px', display: 'block' }}>
                <div className="card-header">
                    <div>
                        <h2 className="card-title">Gestione Proprietà</h2>
                        <p className="card-description">
                            Crea e gestisci le proprietà web da tracciare.
                        </p>
                    </div>
                    {!isAddingNewProperty && !editingPropertyId && (
                        <button 
                            className="button button-primary"
                            onClick={handleAddNewProperty}
                        >
                            Aggiungi Nuova Proprietà
                        </button>
                    )}
                </div>

                {propertyError && (
                    <div className="error-message" style={{ 
                        padding: '12px', 
                        marginBottom: '16px', 
                        backgroundColor: '#fee', 
                        border: '1px solid #fcc',
                        borderRadius: '4px',
                        color: '#c00'
                    }}>
                        {propertyError}
                    </div>
                )}

                {(isAddingNewProperty || editingPropertyId) && (
                    <div className="settings-form" style={{
                        padding: '20px',
                        marginBottom: '20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6'
                    }}>
                        <h3 style={{ marginTop: 0 }}>
                            {isAddingNewProperty ? 'Aggiungi Nuova Proprietà' : 'Modifica Proprietà'}
                        </h3>
                        <div style={{ display: 'grid', gap: '16px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                                    Nome Proprietà
                                </label>
                                <input
                                    type="text"
                                    value={propertyFormData.name}
                                    onChange={(e) => setPropertyFormData({ ...propertyFormData, name: (e.target as HTMLInputElement).value })}
                                    placeholder="es. Sito Web Aziendale"
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                                    Dominio
                                </label>
                                <input
                                    type="text"
                                    value={propertyFormData.domain}
                                    onChange={(e) => setPropertyFormData({ ...propertyFormData, domain: (e.target as HTMLInputElement).value })}
                                    placeholder="es. example.com"
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                />
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                    Inserisci il dominio senza http:// o https://
                                </div>
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                                    Stato
                                </label>
                                <select
                                    value={propertyFormData.status}
                                    onChange={(e) => setPropertyFormData({ ...propertyFormData, status: (e.target as HTMLSelectElement).value as 'active' | 'inactive' })}
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <option value="active">Attivo</option>
                                    <option value="inactive">Inattivo</option>
                                </select>
                            </div>

                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontWeight: 500
                            }}>
                                <input
                                    type="checkbox"
                                    checked={propertyFormData.is_default}
                                    onChange={(e) => setPropertyFormData({
                                        ...propertyFormData,
                                        is_default: (e.currentTarget as HTMLInputElement).checked
                                    })}
                                />
                                Imposta come proprietà predefinita
                            </label>

                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                <button 
                                    className="button button-primary"
                                    onClick={handleSaveProperty}
                                >
                                    Salva
                                </button>
                                <button 
                                    className="button"
                                    onClick={handleCancelPropertyEdit}
                                >
                                    Annulla
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {propertiesLoading ? (
                    <p className="card-description">Caricamento proprietà...</p>
                ) : properties.length === 0 ? (
                    <p className="card-description">Nessuna proprietà configurata. Aggiungi la tua prima proprietà per iniziare.</p>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Dominio</th>
                                <th>ID Proprietà</th>
                                <th>Stato</th>
                                <th>Predefinita</th>
                                <th>Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {properties.map((property) => (
                                <tr key={property.id}>
                                    <td>
                                        <strong>{property.name}</strong>
                                    </td>
                                    <td>
                                        <code style={{ 
                                            backgroundColor: '#f4f4f4', 
                                            padding: '2px 6px', 
                                            borderRadius: '3px',
                                            fontSize: '12px'
                                        }}>
                                            {property.domain}
                                        </code>
                                    </td>
                                    <td>
                                        <code style={{ 
                                            backgroundColor: '#e7f3ff', 
                                            padding: '2px 6px', 
                                            borderRadius: '3px',
                                            fontSize: '11px',
                                            color: '#0066cc'
                                        }}>
                                            {property.id}
                                        </code>
                                    </td>
                                    <td>
                                        <span className={`badge badge-${property.status === 'active' ? 'success' : 'inactive'}`}>
                                            {property.status === 'active' ? 'Attivo' : 'Inattivo'}
                                        </span>
                                    </td>
                                    <td>
                                        {property.is_default ? (
                                            <span className="badge badge-success">Predefinita</span>
                                        ) : (
                                            <span style={{ color: '#666' }}>No</span>
                                        )}
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <button
                                                onClick={() => handleEditProperty(property)}
                                                disabled={!!editingPropertyId || isAddingNewProperty}
                                                style={{
                                                    padding: '6px 12px',
                                                    fontSize: '13px',
                                                    border: '1px solid #0066cc',
                                                    backgroundColor: '#fff',
                                                    color: '#0066cc',
                                                    borderRadius: '4px',
                                                    cursor: editingPropertyId || isAddingNewProperty ? 'not-allowed' : 'pointer',
                                                    opacity: editingPropertyId || isAddingNewProperty ? 0.5 : 1,
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseOver={(e) => {
                                                    if (!editingPropertyId && !isAddingNewProperty) {
                                                        (e.currentTarget as HTMLElement).style.backgroundColor = '#0066cc';
                                                        (e.currentTarget as HTMLElement).style.color = '#fff';
                                                    }
                                                }}
                                                onMouseOut={(e) => {
                                                    (e.currentTarget as HTMLElement).style.backgroundColor = '#fff';
                                                    (e.currentTarget as HTMLElement).style.color = '#0066cc';
                                                }}
                                            >
                                                Modifica
                                            </button>
                                            <button
                                                onClick={() => handleDeleteProperty(property.id)}
                                                disabled={!!editingPropertyId || isAddingNewProperty}
                                                style={{
                                                    padding: '6px 12px',
                                                    fontSize: '13px',
                                                    border: '1px solid #dc3545',
                                                    backgroundColor: '#fff',
                                                    color: '#dc3545',
                                                    borderRadius: '4px',
                                                    cursor: editingPropertyId || isAddingNewProperty ? 'not-allowed' : 'pointer',
                                                    opacity: editingPropertyId || isAddingNewProperty ? 0.5 : 1,
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseOver={(e) => {
                                                    if (!editingPropertyId && !isAddingNewProperty) {
                                                        (e.currentTarget as HTMLElement).style.backgroundColor = '#dc3545';
                                                        (e.currentTarget as HTMLElement).style.color = '#fff';
                                                    }
                                                }}
                                                onMouseOut={(e) => {
                                                    (e.currentTarget as HTMLElement).style.backgroundColor = '#fff';
                                                    (e.currentTarget as HTMLElement).style.color = '#dc3545';
                                                }}
                                            >
                                                Elimina
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default Settings;
